import { FC, useState, MouseEvent } from 'react';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import ProductionViewDialog from 'src/components/DialogBox/ProductionViewDialog';
import ProductionEditDialog from 'src/components/DialogBox/ProductionEditDialog';
import {
  actionButtons,
  productionDialogTitles,
  productionDialogSubtitles,
} from 'src/utils/properties.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { Production } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  deleteProduction,
  getProductionById,
} from 'src/services/production.service';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  MenuItemsWrapper,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';

const ProductionActionMenuButton: FC<{
  production: Production;
  onEditSuccess: () => void;
  onDeleteSuccess: () => void;
}> = ({ production, onEditSuccess, onDeleteSuccess }) => {
  const { showSnackbar } = StyledSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduction, setSelectedProduction] =
    useState<Production | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const handleClick = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
    handleClose();
  };

  const handleView = async () => {
    try {
      const fullProduction = await getProductionById(production.id);
      setSelectedProduction(fullProduction);
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    handleClose();
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setSelectedProduction(null);
  };

  const handleEdit = async () => {
    try {
      const fullProduction = await getProductionById(production.id);
      setSelectedProduction(fullProduction);
      setEditDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    handleClose();
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedProduction(null);
  };

  const handleConfirm = async (status?: string, reason?: string) => {
    try {
      if (dialogType === actionButtons.DELETE) {
        const response = await deleteProduction(production.id, reason || '');
        showSnackbar(response.message, 'success');
        onDeleteSuccess();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
    }
  };

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={handleView}
            iconClassName="bx bx-show"
            text="View"
          />
          <MenuItemWithIcon
            onClick={handleEdit}
            iconClassName="bx bx-edit"
            text="Edit"
          />
          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>
      <ProductionViewDialog
        open={viewDialogOpen}
        handleClose={handleViewDialogClose}
        production={selectedProduction}
        title={productionDialogTitles.VIEW}
        subtitle={productionDialogSubtitles.VIEW}
      />
      <ProductionEditDialog
        open={editDialogOpen}
        production={selectedProduction}
        onClose={handleEditDialogClose}
        onSuccess={onEditSuccess}
      />
      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={productionDialogTitles.DELETE}
        subtitle={`Are you sure you want to delete the production record for "${production.productName}" on ${production.productionDate}? Please provide a reason for deletion.`}
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor="var(--error)"
        alertIconColor="var(--error)"
        dialogType={actionButtons.DELETE}
        rows={1}
        height="410px"
      />
    </>
  );
};

export default ProductionActionMenuButton;
